import React, { useCallback, useEffect, useState } from "react";
import { isEmpty, isEqual } from "lodash";
import { useDispatch } from "react-redux";
import { connect } from "react-redux";
import { matchPath } from "react-router-dom";

import { actions as modelActions } from "features/Model/_ducks";
import { actions as userActions } from "store/users";
import { actions as organizationActions } from "store/organizations";
import { fetchDrawingData } from "./DrawingNew/_ducks/thunk";
import { resetImageGallery } from "store/imageGallery/actions";
import { getImageGallery } from "store/imageGallery/thunk";

import { types } from "features/Model/_ducks";

import { useStateSelector } from "utils/field";
import { categories } from "utils/enclosure";
import { getMemberOrgIds } from "utils/organization/membership";

import { ModelRestricted } from "features/Navigation/PageNotFound";
import Loading from "components/Loading";
import SnackbarAlert from "components/Alert";
import ChbaChecklistDrawerButtonModel from "components/ChbaChecklist/ChbaChecklistDrawerButtonModel";
import CodeDrawer from "./CodeDrawer";
import ModelTabs from "./ModelTabs";

import classes from "./style.module.scss";

const { fetchModelData, modelLoading, clearModelData, checkModelUpdates } = modelActions;
const { fetchUserCodeLib } = userActions;
const { fetchUserOrganization, fetchOrgDir } = organizationActions;

//! Mechanicals ASHP perfomance curves
const Model = ({
    history,
    fetchUserCodeLib,
    clearModelData,
    resetImageGallery,
    modelLoading,
    fetchDrawingData,
    checkModelUpdates,
    fetchModelData,
    getImageGallery,
}) => {
    const dispatch = useDispatch();
    const pathCallback = useCallback(() => {
        return history.location.pathname;
    }, [history.location]);

    const [CHBAProjectChecklistOpen, toggleProjectChecklistOpen] = useState(false);
    const [isInitialLoading, setInitialLoading] = useState(true);
    const [submitting, setIsSubmitting] = useState(false);

    const { location: { pathname = "" } = {} } = history || {};

    const { params: { modelId } = {} } =
        matchPath(pathname, {
            path: "/models/:modelId",
        }) || {};

    const {
        uid,
        userPermissions: { CHBA_NZ_ADMIN = false, CHBA_NZEA = false, CHBA_NZ_ASSIST = false } = {},
        codeLib = {},
    } = useStateSelector("user");
    const model = useStateSelector("model") || {};
    const organization = useStateSelector("organization") || {};
    const orgIds = getMemberOrgIds(organization);
    const modelDir = useStateSelector("modelDir") || {};
    const drawing = useStateSelector("drawing") || {};
    const results = useStateSelector("results") || {};
    const upgrades = useStateSelector("upgrades") || {};

    const {
        loading,
        modelData,
        modelTakeoff,
        lastSnapshot,
        modelDetailsToCompare,
        isSnapshotUpdating,
        modelDataToCompare,
        modelTakeoffToCompare,
        modelDetails,
        isSharingModel,
        isServerSyncing,
        modelNotFound = false,
    } = model;
    //
    const { isSimulationRunning, simulationStatus = "IDLE" } = modelDetails;
    const { loading: isResultsLoading = false } = results;
    const { loading: isUpgradesLoading = false, initPackages = {} } = upgrades;

    const chbaFileTagged = model?.modelData?.chbaNz?.file?.targetLabel?.id != null;

    const snapshotModelData = lastSnapshot.modelData || {};
    const snapshotModelDetails = lastSnapshot.modelDetails || {};
    const snapshotTakeoff = lastSnapshot.modelTakeoff || {};
    const upgradesSnapshot = upgrades.initPackagesSnapshot || {};
    const { imageDataToCompare = {}, imageDataSnapshot = {}, saving: isDrawingSaving = false } = drawing || {};

    const storeysId = modelData?.specifications?.numStoreys?.id;
    const isNewSnapshot =
        !isEqual(
            {
                ...snapshotModelDetails,
                simulationStatus: "IDLE",
                lastEdited: {},
                lastCliRun: {},
                writePermission: [],
                sharedWith: [],
                modelErrorSubmitted: false,
                errorDetails: {},
                isInsightsActive: false,
                isRxRResultsUnlocked: false,
            },
            {
                ...modelDetailsToCompare,
                simulationStatus: "IDLE",
                lastEdited: {},
                lastCliRun: {},
                writePermission: [],
                sharedWith: [],
                modelErrorSubmitted: false,
                errorDetails: {},
                isInsightsActive: false,
                isRxRResultsUnlocked: false,
            }
        ) ||
        //? Not sure if this is the best solution, but I tested it and it works, and doesn't miss any changes
        !isEqual(
            { ...snapshotModelData, packageResults: {}, program: { ...snapshotModelData.program, results: {} } },
            { ...modelDataToCompare, packageResults: {}, program: { ...modelDataToCompare.program, results: {} } }
        ) ||
        !isEqual(modelTakeoffToCompare, snapshotTakeoff) ||
        !isEqual(imageDataToCompare, imageDataSnapshot) ||
        !isEqual(initPackages, upgradesSnapshot);

    const handleAddTakeoff = (compType = "", id, name = "") => {
        dispatch({
            type: types.ADD_TAKEOFF_COMP,
            comp: id,
            compType: compType || name,
        });
    };

    const fetchModel = useCallback(async () => {
        if (modelId && uid) {
            await modelLoading();
            await fetchDrawingData(modelId);
            await checkModelUpdates(modelId);
            await fetchModelData(modelId);
            await getImageGallery(modelId, uid);
        }
    }, [modelId, uid, modelLoading, fetchDrawingData, checkModelUpdates, fetchModelData, getImageGallery]);

    useEffect(() => {
        if (modelId && isEmpty(modelData) && !loading && uid && isInitialLoading) {
            // Temporary fix for double fetchModel on page refresh to prevent overwriting roomSummary
            fetchModel();
        }

        if (!loading) setInitialLoading(false);
    }, []);

    useEffect(() => {
        const unsubscribe = dispatch(modelActions.checkModelUpdates(modelId));

        return () => {
            if (unsubscribe) unsubscribe();

            // Navigating to Insights keeps the same model in the URL; do not clear Redux
            // or the insights view briefly sees an empty modelId and misses fetches.
            if (history?.location?.pathname?.startsWith("/insights/")) {
                return;
            }
            clearModelData();
            resetImageGallery();
        };
    }, []);

    useEffect(() => {
        if (modelData) {
            const storeyCategories = categories(storeysId) || [];

            for (let i = 0; i < storeyCategories.length; i++) {
                const { id, name } = storeyCategories[i];

                if (!modelTakeoff[id]) {
                    if (id === "foundation" && isEmpty(modelTakeoff[id])) {
                        handleAddTakeoff("basement", id);
                    }
                }

                if (id !== "foundation" && isEmpty(modelTakeoff[id])) {
                    handleAddTakeoff("", id, name);
                }
            }
        }
    }, [modelData]);

    useEffect(() => {
        if (isEmpty(codeLib)) {
            fetchUserCodeLib(uid);
        }
    }, [codeLib, uid, fetchUserCodeLib]);

    // Whether an organization's codes may be used in this model is the organization's own setting,
    // and when it says no, only the models in its shared folder may use them. Both answers live in
    // the organization slice, which nothing else on a model page reads.
    useEffect(() => {
        if (!uid) return;

        orgIds.forEach((orgId) => {
            if (!organization?.details?.[orgId]) dispatch(fetchUserOrganization(uid, orgId));

            dispatch(fetchOrgDir(orgId, true));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid, orgIds.join(",")]);

    if (loading || isInitialLoading) {
        return <Loading className={classes.spinner} />;
    }

    if (!loading && modelNotFound) {
        return <ModelRestricted />;
    }

    const chbaRoadmapEnabled = (CHBA_NZ_ADMIN || CHBA_NZEA || CHBA_NZ_ASSIST) && chbaFileTagged;

    return (
        <div style={{ position: "relative" }}>
            <SnackbarAlert
                open={
                    isNewSnapshot &&
                    !isSnapshotUpdating &&
                    !submitting &&
                    !isSimulationRunning &&
                    !loading &&
                    !isResultsLoading &&
                    !isUpgradesLoading &&
                    !isDrawingSaving &&
                    !isSharingModel &&
                    !isServerSyncing &&
                    simulationStatus !== "RUNNING"
                }
                message={
                    "Changes to the model have been detected. Save your model to merge changes or refresh to discard changes."
                }
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                direction="down"
                severity="error"
                onExited={() => {}}
                top={"60px"}
            />
            {/* <button
                onClick={() => {
                    toggleProjectChecklistOpen(!CHBAProjectChecklistOpen);
                }}
                className={classes.checklistDrawer}
            >
                <div className={classes.drawerButtonText}>
                    <p>CHBA NZ Reno</p>
                </div>
            </button> */}
            {chbaRoadmapEnabled && (
                <ChbaChecklistDrawerButtonModel
                    open={CHBAProjectChecklistOpen}
                    toggleOpen={toggleProjectChecklistOpen}
                />
            )}
            <ModelTabs
                history={history}
                pathCallback={pathCallback}
                CHBAProjectChecklistOpen={CHBAProjectChecklistOpen}
                toggleProjectChecklistOpen={toggleProjectChecklistOpen}
                modelDir={modelDir}
                isNewSnapshot={isNewSnapshot && !isSnapshotUpdating && !isServerSyncing && !submitting}
                fetchModelData={fetchModel}
                isSyncing={loading}
                submitting={submitting}
                setSubmitting={setIsSubmitting}
            />
            <CodeDrawer />
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    fetchUserCodeLib: (uid) => dispatch(fetchUserCodeLib(uid)),
    clearModelData: () => dispatch(clearModelData()),
    resetImageGallery: () => dispatch(resetImageGallery()),
    modelLoading: () => dispatch(modelLoading()),
    fetchDrawingData: (modelId) => dispatch(fetchDrawingData(modelId)),
    checkModelUpdates: (modelId) => dispatch(checkModelUpdates(modelId)),
    fetchModelData: (modelId) => dispatch(fetchModelData(modelId)),
    getImageGallery: (modelId, uid) => dispatch(getImageGallery(modelId, uid)),
});

export default connect(null, mapDispatchToProps)(Model);
